import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
var _excluded = ["countries", "disabled", "error", "formatAssetsUrl", "inputProps", "label", "placeholder", "currentLang", "selectProps", "name", "value", "onChange", "dataTestId", "useTooltip"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Flex, FormControl, FormLabel, Image, Input, InputGroup, InputLeftElement, useTheme, useDisclosure, useOutsideClick } from '@chakra-ui/react';
import { getInputTypographyOverride, getSortedCountriesByCurrentLang, GLOBALS } from '@whitbread-eos/utils';
import dynamic from 'next/dynamic';
import { forwardRef, memo, useCallback, useMemo, useRef } from 'react';
import ChevronDown from '../../assets/icons/ChevronDown';
import ChevronUp from '../../assets/icons/ChevronUp';
import { formatDataTestId } from '../../utils/formatters';
import Icon from '../Icon';
import CountriesList from '../PhoneInput/CountriesList';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var Tooltip = dynamic(_asyncToGenerator(function* () {
  var _yield$import = yield import('../Tooltip/Tooltip.component'),
    Tooltip = _yield$import.default;
  return {
    default: Tooltip
  };
}), {
  ssr: false
});
function CountryInput(_ref2, forwardedRef) {
  var _country$countryName;
  var _ref2$countries = _ref2.countries,
    countries = _ref2$countries === void 0 ? [] : _ref2$countries,
    disabled = _ref2.disabled,
    error = _ref2.error,
    _ref2$formatAssetsUrl = _ref2.formatAssetsUrl,
    formatAssetsUrl = _ref2$formatAssetsUrl === void 0 ? url => url : _ref2$formatAssetsUrl,
    inputProps = _ref2.inputProps,
    label = _ref2.label,
    placeholder = _ref2.placeholder,
    currentLang = _ref2.currentLang,
    selectProps = _ref2.selectProps,
    name = _ref2.name,
    value = _ref2.value,
    onChange = _ref2.onChange,
    dataTestId = _ref2.dataTestId,
    _ref2$useTooltip = _ref2.useTooltip,
    useTooltip = _ref2$useTooltip === void 0 ? false : _ref2$useTooltip,
    rest = _objectWithoutProperties(_ref2, _excluded);
  var theme = useTheme();
  var ref = useRef(null);
  var _useDisclosure = useDisclosure(),
    isOpen = _useDisclosure.isOpen,
    onToggle = _useDisclosure.onToggle,
    onClose = _useDisclosure.onClose;
  var displayLabel = label && !disabled;
  var baseDataTestId = dataTestId;
  var inputTypographyOverride = getInputTypographyOverride(inputProps, theme === null || theme === void 0 ? void 0 : theme.textStyles);
  useOutsideClick({
    ref: ref,
    handler: () => onClose()
  });
  var language = currentLang || GLOBALS.language.EN;
  var country = useMemo(() => countries.find(c => c.countryCode === (value === null || value === void 0 ? void 0 : value.countryCode)), [countries, value]);
  var sortedCountries = getSortedCountriesByCurrentLang(countries, language);
  var handleCountryChange = useCallback(item => {
    onChange(item);
    onClose();
  }, [onChange, onClose]);
  return _jsxs(FormControl, _objectSpread(_objectSpread(_objectSpread({}, rootStyle), {}, {
    isInvalid: !!error,
    ref: ref
  }, _objectSpread({}, rest)), {}, {
    children: [_jsx(Tooltip, _objectSpread(_objectSpread({}, tooltipStyle), {}, {
      description: error !== null && error !== void 0 ? error : '',
      variant: "inlineError",
      placement: "bottom-start",
      isOpen: useTooltip,
      children: _jsx(_Fragment, {
        children: _jsxs(InputGroup, {
          tabIndex: 0,
          sx: _objectSpread({}, groupInputStyles),
          children: [_jsx(InputLeftElement, _objectSpread(_objectSpread({
            "data-testid": formatDataTestId(baseDataTestId, 'countrySelector')
          }, selectorStyle()), {}, {
            role: "button",
            type: "button",
            onClick: onToggle,
            children: country && _jsx(Flex, {
              "data-testid": formatDataTestId(baseDataTestId, 'countryFlag'),
              pl: 'xmd',
              children: _jsx(Image, {
                borderRadius: "full",
                boxSize: "1.5rem",
                src: formatAssetsUrl(country.flagSrc),
                title: country.countryName
              })
            })
          })), displayLabel && _jsx(FormLabel, _objectSpread(_objectSpread({
            "data-testid": formatDataTestId(baseDataTestId, "".concat(name, "-label")),
            pos: "absolute"
          }, labelStyle()), {}, {
            htmlFor: name,
            children: label
          })), _jsx(Input, _objectSpread(_objectSpread(_objectSpread(_objectSpread({
            disabled: disabled,
            placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : label,
            ref: forwardedRef,
            name: name,
            value: (_country$countryName = country === null || country === void 0 ? void 0 : country.countryName) !== null && _country$countryName !== void 0 ? _country$countryName : '',
            readOnly: true
          }, inputProps), inputTypographyOverride), countryInputStyles), {}, {
            "data-testid": formatDataTestId(baseDataTestId, 'countryName')
          })), _jsx(Icon, _objectSpread({
            onClick: onToggle,
            mx: "2",
            svg: isOpen ? _jsx(ChevronUp, {
              "aria-label": "chevron-icon-up"
            }) : _jsx(ChevronDown, {
              "aria-label": "chevron-icon-down"
            })
          }, countryInputIconStyles))]
        })
      })
    })), isOpen ? _jsx(CountriesList, _objectSpread({
      formatAssetsUrl: formatAssetsUrl,
      options: sortedCountries,
      onChange: handleCountryChange,
      showDialingCode: false
    }, selectProps)) : null]
  }));
}
var countryInputStyles = {
  h: '3.5rem',
  borderRadius: 'md',
  borderColor: 'lightGrey1',
  _focus: {
    borderColor: 'primary',
    boxShadow: '0 0 0 1px var(--chakra-colors-primary)'
  },
  paddingLeft: '3.2rem'
};
var countryInputIconStyles = {
  cursor: 'pointer',
  position: 'absolute',
  right: '0.5rem',
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'darkGrey1',
  _hover: {
    color: 'primary'
  },
  _focus: {
    color: 'primary'
  }
};
var selectorStyle = () => ({
  cursor: 'pointer',
  h: 'full',
  justifyContent: 'flex-start',
  pl: '2',
  width: '100%'
});
var groupInputStyles = {
  borderRadius: 'md',
  '&:focus-within': {
    borderColor: 'var(--chakra-colors-primary)',
    borderWidth: '1px',
    boxShadow: '0 0 0 1px var(--chakra-colors-primary)'
  }
};
var tooltipStyle = {
  h: '2.25rem',
  display: 'flex',
  alignContent: 'center'
};
var labelStyle = () => {
  return {
    h: '1.25rem',
    w: 'fit-content',
    fontSize: 'sm',
    align: 'center',
    px: 'xs',
    ml: '0.750rem',
    top: '-0.625rem',
    backgroundColor: 'baseWhite',
    zIndex: '1',
    color: 'darkGrey1',
    display: 'block',
    _focus: {
      color: 'primary',
      display: 'block'
    }
  };
};
var rootStyle = {
  maxW: '26.25rem'
};
export default memo(forwardRef(CountryInput));